<template>
    <div class="h-100">
        <loader :loading="true" v-if="loading"></loader>
        <div class="d-flex flex-column justify-content-end h-100">
            <div class="text">Enter a reference to add to the selection:</div>
            <br />
            <div class="row">
                <div class="col pr-0"><input class="w-100" type="text" :disabled="loading" @keyup.enter="newSearch" v-model="itemSearchTerm" /></div>
                <div class="col-auto"><ForcastButton :size="'l'" @click="newSearch" :disabled="!itemSearchTerm">Add it</ForcastButton></div>
            </div>
        </div>

        <div v-if="isActive">
            <ModalDefault
                size="xl"
                :with-content="true"
                @close="isActive = false"
                :theme="true"
                :fix-height="false"
                :fix-width="false"
                :loading="loading"
            >
                <template slot="main">
                    <div class="row result" v-if="total !== null">
                        <div class="col-sm-12" style="text-align: center">
                            <span class="number-of-items">
                                <span class="total">{{ total }}</span> items found
                            </span>
                        </div>
                    </div>
                    <div class="row item-select align-items-center">
                        <div class="col-auto">
                            <div :style="{ visibility: currentPage > 1 ? 'visible' : 'hidden' }" @click="currentPage--">
                                <ForcastButton>Prev</ForcastButton>
                            </div>
                        </div>
                        <div class="col">
                            <div class="inner row">
                                <div v-for="item in paginatedItems" :key="item.id" class="col">
                                    <ItemSearchSingle
                                        :item="item"
                                        @item-added="addItem(item)"
                                        :disabled="!canAddItem(item)"
                                        :forecast-windows="forecastWindows"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="col-auto">
                            <div :style="{ visibility: hasNextPage ? 'visible' : 'hidden' }">
                                <ForcastButton @click="goToNextPage">View<br />more<br />results</ForcastButton>
                            </div>
                        </div>
                    </div>
                </template>
            </ModalDefault>
        </div>
    </div>
</template>
<script>
import ItemSearchSingle from "@/components/forecasts/pages/sections/itemSearch/ItemSearchSingle";
import loader from "@/components/loader/loader";
import ForecastValidator from "@/components/forecasts/validation/forecastValidator";
import forecastsService from "@/components/forecasts/forecastsService";
import ForcastButton from "@/components/forecasts/elements/form/ForcastButton";
import ModalContainer from "@/plano/components/modal-container";
import ModalDefault from "@/components/ui/ModalDefault";

export default {
    components: {
        ModalDefault,
        ModalContainer,
        ForcastButton,
        ItemSearchSingle,
        loader
    },
    props: {
        forecast: {
            type: Object,
            required: true
        },
        forecastWindows: {
            type: Array,
            required: true
        },
        excludedItems: {
            type: Array,
            required: false,
            default: []
        },
        resetSearch: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            items: [],
            itemSearchTerm: "",
            currentPage: 1,
            maxNumberOfVisibleItems: 5,
            loading: false,
            isActive: false,
            total: null,
            request: {
                currentPage: 1,
                limit: 15,
                previousPage: null,
                nextPage: null
            }
        };
    },
    watch: {
        resetSearch(value) {
            if (value) {
                this.itemSearchTerm = "";
                this.$emit("resetSearch", false);
            }
        }
    },
    computed: {
        hasNextPage() {
            return this.maxNumberOfVisibleItems * this.currentPage < this.total;
        },
        paginatedItems() {
            let items = this.items;
            let numberOfItems = items.length;

            if (numberOfItems > this.maxNumberOfVisibleItems) {
                let start = this.maxNumberOfVisibleItems * (this.currentPage - 1);
                let end = start + this.maxNumberOfVisibleItems;
                return items.slice(start, end);
            }
            return items;
        },
        searchAlreadyExistRefItem() {
            let itemSearchTermUpdated = "";
            if (this.itemSearchTerm.indexOf("0000000") === 0) {
                itemSearchTermUpdated = this.itemSearchTerm.substr(7, this.itemSearchTerm.length);
            } else {
                itemSearchTermUpdated = "0000000" + this.itemSearchTerm;
            }
            console.log(this.itemSearchTerm);
            console.log(itemSearchTermUpdated);
            return this.excludedItems.some(i => {
                return (
                    i.clientSapRef === this.itemSearchTerm ||
                    i.clientSapRef === itemSearchTermUpdated ||
                    i.clientEanRef === this.itemSearchTerm ||
                    i.clientEanRef === itemSearchTermUpdated
                );
            });
        }
    },
    created() {
        this.init();
    },
    methods: {
        init() {
            this.currentPage = 1;
            this.items = [];
            this.total = null;
            this.request.currentPage = 1;
            this.request.previousPage = null;
            this.request.nextPage = null;
        },
        newSearch() {
            if (this.searchAlreadyExistRefItem) {
                this.dialogError("This Item is already in this forecast");
            } else {
                this.init();
                this.searchItems();
            }
        },
        async searchItems() {
            if (!this.itemSearchTerm) {
                return;
            }
            this.loading = true;
            let result = await forecastsService.searchItems({
                searchTerm: this.itemSearchTerm,
                limit: this.request.limit,
                page: this.request.currentPage,
                // only active, obsolete and cancelled components:
                status: [3, 4, 5]
            });
            this.items = this.items.concat(result.data);
            this.total = result.meta.total;
            this.request.previousPage = result.meta.previousPage;
            this.request.nextPage = result.meta.nextPage;
            if (this.items.length > 1) {
                this.isActive = true;
            }
            if (this.items.length === 1) {
                this.$emit("item-added", this.items[0], true);
            }
            this.loading = false;
        },
        addItem(item) {
            this.$emit("item-added", item, false);
        },
        canAddItem(item) {
            if (this.excludedItems.some(eItem => eItem.id === item.id)) {
                return false;
            }
            return ForecastValidator.canAddItem(this.forecast, [], item);
        },
        async goToNextPage() {
            this.currentPage++;
            if (this.maxNumberOfVisibleItems * this.currentPage > this.items.length && this.items.length < this.total) {
                this.request.currentPage++;
                await this.searchItems();
            }
        },
        closeModal() {
            this.$emit("closed");
        }
    }
};
</script>
<style scoped lang="scss">
@import "../../../css/forecasts";
.number-of-items {
    padding-left: 20px;
    .total {
        font-size: 1.2em;
        font-weight: bold;
    }
}
.text {
    font-weight: bold;
    text-transform: none;
}
.result {
    margin-top: 15px;
    text-align: center;
    padding: 20px;
}
.item-select {
    min-height: 200px;
    div {
        padding: 10px;
    }
}
input {
    border: 1px solid;
    border-radius: 3px;
    color: #a2a2a2;
    font-size: 18px;
    padding: 6px 15px;
}
</style>



// WEBPACK FOOTER //
// ItemSearch.vue?1c785be2